import { colors } from './colors';

/** Semantic metric colors — blue reserved for interactive/traffic */
export const WEB_ANALYTICS_COLORS = {
  page_views: '#2563eb', // traffic blue
  unique_visitors: '#10b981', // engagement green
  sessions: '#8b5cf6', // purple
  events: '#0ea5e9', // sky
  bounce: '#f59e0b', // orange
  devices: '#6366f1', // indigo
  referrers: '#06b6d4', // cyan
  countries: '#14b8a6', // teal
  browsers: '#3b82f6',
  scroll_depth: '#06b6d4',
  session_start: '#8b5cf6',
  session_end: '#a78bfa',
  time_on_page: '#14b8a6',
  chat_open: '#10b981',
  chat_close: '#ef4444',
  chat_minimize: '#6ee7b7',
  identify: '#f59e0b',
  custom: colors.text.secondary,
  direct: colors.text.secondary,
};

export const WA_SURFACE = {
  border: '#E5E7EB',
  shadow: '0 1px 3px rgba(0,0,0,0.05)',
  radius: 16,
  cardPadding: 24,
  sectionGap: 32,
};

export function eventColor(eventName) {
  const key = String(eventName || 'custom').toLowerCase();
  if (WEB_ANALYTICS_COLORS[key]) return WEB_ANALYTICS_COLORS[key];
  if (key.startsWith('chat_')) return WEB_ANALYTICS_COLORS.chat_open;
  return WEB_ANALYTICS_COLORS.custom;
}

export function formatEventLabel(eventName) {
  const key = String(eventName || 'custom').toLowerCase();
  const labels = {
    page_view: 'Page Views',
    session_start: 'Session Start',
    session_end: 'Session End',
    scroll_depth: 'Scroll Depth',
    time_on_page: 'Time on Page',
    chat_open: 'Chat Open',
    chat_close: 'Chat Close',
    chat_minimize: 'Chat Minimize',
    identify: 'Identify',
    custom: 'Custom',
  };
  if (labels[key]) return labels[key];
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function eventIcon(eventName) {
  const key = String(eventName || 'custom').toLowerCase();
  const icons = {
    page_view: '👁',
    session_start: '▶',
    session_end: '⏹',
    scroll_depth: '📜',
    time_on_page: '⏱',
    chat_open: '💬',
    chat_close: '❌',
    chat_minimize: '➖',
    identify: '👤',
    custom: '✦',
  };
  return icons[key] || icons.custom;
}

/** Round max up to a clean axis ceiling (4–5 nice ticks). */
export function niceAxisMax(rawMax) {
  const max = Number(rawMax) || 0;
  if (max <= 0) return 4;
  const padded = max;
  const exp = Math.floor(Math.log10(padded));
  const magnitude = Math.pow(10, exp);
  const normalized = padded / magnitude;
  let niceNorm;
  if (normalized <= 1) niceNorm = 1;
  else if (normalized <= 2) niceNorm = 2;
  else if (normalized <= 2.5) niceNorm = 2.5;
  else if (normalized <= 5) niceNorm = 5;
  else niceNorm = 10;
  return niceNorm * magnitude;
}

export function buildYTicks(maxValue, tickCount = 5) {
  const top = niceAxisMax(maxValue);
  const steps = Math.max(2, tickCount - 1);
  const ticks = [];
  for (let i = 0; i <= steps; i++) {
    ticks.push(Math.round((top * i) / steps));
  }
  return { domainMax: top, ticks: [...new Set(ticks)] };
}

/**
 * Which X indices to show labels for, based on range length.
 * 7d → every day; 30d → every 3rd; 90d → weekly; longer → monthly-ish.
 */
export function shouldShowXTick(index, total, days) {
  if (total <= 1) return true;
  if (index === 0 || index === total - 1) return true;
  const range = Number(days) || total;
  if (range <= 7) return true;
  if (range <= 30) return index % 3 === 0;
  if (range <= 90) return index % 7 === 0;
  // ~monthly for longer ranges
  return index % 30 === 0;
}

export function formatTrendLabel(dateStr, days) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  const range = Number(days) || 30;
  if (range > 90) {
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
